import React from 'react';
import { connect } from 'react-redux';
import { setLocale } from '../../actions/intl';
import {
  t, PHONE, PHONE_LABEL, initReveal,
  IconPhone, IconTick,
  Header, Footer, CallBar
} from './SiteChrome';
import CoverageMap from './CoverageMap';

/*
 * Halaman Coverage — port dari site/coverage.html.
 *
 * Angka (menit dan jumlah truk) sengaja HARDCODED di sini, sama seperti
 * prototipe. Datanya sebetulnya ada di tabel ServiceArea/Location, jadi ini
 * kandidat kuat untuk disambungkan ke GraphQL nanti — tapi menyambungkannya
 * sekarang berarti menebak-nebak bentuk data sebelum client mengonfirmasi
 * angkanya. Lihat catatan di copy.json.
 */

// [kunci kota, menit, jumlah truk]
const ZONES = [
  ['dubai', 25, 18], ['abudhabi', 30, 9], ['sharjah', 28, 11], ['ajman', 35, 5],
  ['rak', 40, 4], ['fujairah', 45, 3], ['uaq', 40, 4], ['alain', 35, 4]
];

// [kunci area, teks catatan siap pakai]
const DUBAI_AREAS = [
  ['marina', '~18 min'], ['business', '~20 min'], ['quoz', '~20 min'], ['deira', '~22 min'],
  ['jebelali', '~25 min'], ['mirdif', '~25 min'], ['silicon', '~28 min'], ['e11', null]
];

const HIGHWAY = [1, 2, 3, 4, 5];

const LD_SERVICE_AREA = {
  '@context': 'https://schema.org',
  '@type': 'AutoRepair',
  '@id': 'https://basrecovery.com/#business',
  name: 'Bas Recovery',
  telephone: PHONE,
  url: 'https://basrecovery.com/coverage',
  areaServed: ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah',
    'Fujairah', 'Umm Al Quwain', 'Al Ain'].map(name => ({ '@type': 'City', name }))
};

class Coverage extends React.Component {
  state = { navOpen: false };

  toggleNav = () => this.setState(s => ({ navOpen: !s.navOpen }));
  closeNav = () => this.setState({ navOpen: false });

  switchLang = () => {
    const next = this.props.currentLocale === 'ar' ? 'en-US' : 'ar';
    document.documentElement.lang = next;
    this.props.setLocale({ locale: next });
  };

  componentDidMount() {
    this.onKey = e => { if (e.key === 'Escape') this.closeNav(); };
    document.addEventListener('keydown', this.onKey);
    this.io = initReveal();
  }
  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKey);
    if (this.io) this.io.disconnect();
  }

  render() {
    const { navOpen } = this.state;
    const L = this.props.currentLocale || 'en-US';
    const tx = (k, v) => t(L, k, v);

    return (
      <div>
        <a className="skip" href="#main">{tx('nav.skip')}</a>

        <Header
          locale={L} current="/coverage"
          navOpen={navOpen}
          onToggleNav={this.toggleNav}
          onCloseNav={this.closeNav}
          onSwitchLang={this.switchLang}
        />

        <main id="main">
          <section className="hero" style={{ paddingBlock: 'clamp(40px,6vw,68px) clamp(44px,6vw,72px)' }}>
            <div className="wrap">
              <p className="eyebrow">{tx('nav.coverage')}</p>
              <h1 style={{ fontSize: 'var(--fs-h1)', maxWidth: '24ch' }}>{tx('cvg.h1')}</h1>
              <p className="lead" style={{ maxWidth: '56ch' }}>{tx('cvg.lead')}</p>
              <div className="hero-cta">
                <a className="btn btn-call btn-lg" href={`tel:${PHONE}`}>
                  <IconPhone />{tx('hero.call', { phone: PHONE_LABEL })}
                </a>
              </div>
            </div>
          </section>

          {/* ── ZONA KOTA ── */}
          <section>
            <div className="wrap">
              <div className="sec-head">
                <h2>{tx('cvg.zones.h2')}</h2>
                <p className="lead">{tx('cvg.zones.lead')}</p>
              </div>
              <div className="cov-grid">
                {ZONES.map(([key, mins, trucks]) => (
                  <div className="cov" key={key}>
                    <IconTick />
                    <span>{tx(`cov.${key}`)}<small>{tx('cvg.zoneNote', { n: mins, t: trucks })}</small></span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── AREA DUBAI ── */}
          <section className="band-warm">
            <div className="wrap">
              <div className="sec-head">
                <h2>{tx('cvg.dubai.h2')}</h2>
                <p className="lead">{tx('cvg.dubai.lead')}</p>
              </div>
              <div className="cov-grid">
                {DUBAI_AREAS.map(([key, note]) => (
                  <div className="cov" key={key}>
                    <IconTick />
                    <span>{tx(`cvg.area.${key}`)}<small>{note || tx('cvg.area.e11.note')}</small></span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── JALAN TOL, GURUN, ANTAR-EMIRAT ── */}
          <section>
            <div className="wrap split">
              <div>
                <h2>{tx('cvg.road.h2')}</h2>
                <p className="lead">{tx('cvg.road.lead')}</p>
                <div className="ticks">
                  {HIGHWAY.map(n => (
                    <div className="tick" key={n}>
                      <IconTick /><span><strong>{tx(`cvg.road.${n}b`)}</strong>{tx(`cvg.road.${n}`)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <CoverageMap locale={L} />
            </div>
          </section>

          {/* ── CTA ── */}
          <section className="band-night">
            <div className="wrap" style={{ textAlign: 'center' }}>
              <h2 style={{ maxWidth: '26ch', marginInline: 'auto' }}>{tx('cvg.cta.h2')}</h2>
              <p style={{ maxWidth: '48ch', margin: 'var(--sp-4) auto var(--sp-6)' }}>{tx('cvg.cta.lead')}</p>
              <a className="btn btn-call btn-lg" href={`tel:${PHONE}`}>
                <IconPhone />{tx('hero.call', { phone: PHONE_LABEL })}
              </a>
            </div>
          </section>
        </main>

        <Footer locale={L} />
        <CallBar locale={L} />

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(LD_SERVICE_AREA) }} />
      </div>
    );
  }
}

const mapState = state => ({ currentLocale: state?.intl?.locale });
const mapDispatch = { setLocale };

export default connect(mapState, mapDispatch)(Coverage);
